import AIcon from '@/components/A/AIcon/AIcon'
import styles from './VideoAnalysis.module.less'
import cls from 'classnames'
import VideoPlayer from './VideoPlayer/VideoPlayer'
import { createContext, useContext, useEffect, useId, useState } from 'react'
import MarkdownRenderer from '@/components/MarkdownRenderer/MarkdownRenderer'
import { useEventListener, useInterval } from 'ahooks'
import { FormTitle } from '@/pages/ProjectCreatePage/components/StepOne/StepOne'
import AnalysisStep from '../AnalysisStep/AnalysisStep'
import ScoreCard from './ScoreCard/ScoreCard'
import { useTranslation } from 'react-i18next'
import SubAnalysisDescriptionPopover from './SubAnalysisDescriptionPopover/SubAnalysisDescriptionPopover'
import VideoScoreBarChart from './VideoScoreBarChart/VideoScoreBarChart'
import MiddleText from '@/components/MiddleText/MiddleText'
import {
  PROFESSOR_RATE_BTN_ID_PREFIX,
  RateBtn,
} from '@/pages/ProfessorProjectDetailPage/components/ProfessorRatePanel/ProfessorRatePanel'
import { ProfessorContext } from '@/Contexts/Contexts'
import { DASHBOARD_CONTENT_CONTAINER_ID } from '@/router/pageConst'
import { CATEGORY_TAB_ID } from '../CategoryTab/CategoryTab'
import HiddenDetailBox from '../StoryboardAnalysis/components/HiddenDetailBox/HiddenDetailBox'
import Player from 'xgplayer/es/player'
import AiTip from '@/components/AiTip/AiTip'
import { INTERNAL_HOST } from '@/commonConst'
import useProductType from '@/helpers/commonHooks/useProductType'
import { isPortraitByProjectInfo, isSurveyProductCategory } from '@/helpers/productCategory'
import SubjectiveResponseCurveLandscape from './components/SubjectiveResponseCurveLandscape/SubjectiveResponseCurveLandscape'
import SubjectiveResponseCurvePortrait from './components/SubjectiveResponseCurvePortrait/SubjectiveResponseCurvePortrait'
// import { blobGet } from '@/api/core'
// import TestChart from './TestChart/TestChart'

export function useAnalysisLabels(hasTranslated: boolean) {
  const { t, i18n } = useTranslation()
  const [analysisLabels, setAnalysisLabels] = useState<
    Array<{ name: string; desc: string; periods: number[] }>
  >([])
  useEffect(() => {
    const normalPeriods = [
      [0, 20],
      [21, 40],
      [41, 60],
      [61, 99],
    ]
    const translatedPeriods = [
      [0, 3],
      [4, 6],
      [7, 9],
      [10, 11],
    ]
    const ANALYSIS_LABELS = [
      {
        name: t('dashboard.projectDetailPage.videoView.analysisStepTitle1'),
        desc: t('dashboard.projectDetailPage.videoView.analysisStepDesc1'),
        // periods: [0, 20],
      },
      {
        name: t('dashboard.projectDetailPage.videoView.analysisStepTitle2'),
        desc: t('dashboard.projectDetailPage.videoView.analysisStepDesc2'),
        // periods: [21, 40],
      },
      {
        name: t('dashboard.projectDetailPage.videoView.analysisStepTitle3'),
        desc: t('dashboard.projectDetailPage.videoView.analysisStepDesc3'),
        // periods: [41, 60],
      },
      {
        name: t('dashboard.projectDetailPage.videoView.analysisStepTitle4'),
        desc: t('dashboard.projectDetailPage.videoView.analysisStepDesc4'),
        // periods: [61, 90],
      },
    ]
    if (hasTranslated) {
      setAnalysisLabels(
        ANALYSIS_LABELS.map((label, index) => {
          return {
            ...label,
            periods: normalPeriods[index],
          }
        })
      )
    } else {
      setAnalysisLabels(
        ANALYSIS_LABELS.map((label, index) => {
          return {
            ...label,
            periods: translatedPeriods[index],
          }
        })
      )
    }
  }, [hasTranslated, i18n.language])

  return { ANALYSIS_LABELS: analysisLabels }
}

export function useTranslateDimension() {
  const { t } = useTranslation()
  const { productType } = useProductType()
  const NORMAL_PATH = 'dashboard.projectDetailPage.videoView'
  const EXPERIENCE_PATH = 'dashboard.experience.projectDetailPage.flowAnalysis'
  const EYE_MOVEMENT_RATIO = isSurveyProductCategory(productType)
    ? t(`${EXPERIENCE_PATH}.terminology.gazeRecognition`)
    : t(`${NORMAL_PATH}.eyeMovementRatio`)
  const COGNITIVE_ENGAGEMENT = isSurveyProductCategory(productType)
    ? t(`${EXPERIENCE_PATH}.terminology.engagementRecognition`)
    : t(`${NORMAL_PATH}.cognitiveEngagement`)
  const EMOTION_RECOGNITION = isSurveyProductCategory(productType)
    ? t(`${EXPERIENCE_PATH}.terminology.emotionRecognition`)
    : t(`${NORMAL_PATH}.emotionRecognition`)

  return { EYE_MOVEMENT_RATIO, COGNITIVE_ENGAGEMENT, EMOTION_RECOGNITION }
}

function useTranslateProgress(hasTranslated: boolean) {
  const [ratio, setRatio] = useState<number>(0)
  const [delay, setDelay] = useState<number | undefined>(undefined)

  useInterval(() => {
    if (ratio >= 10) {
      setRatio(10)
    } else {
      setRatio((ratio) => ratio + 1)
    }
  }, delay)

  useEffect(() => {
    if (hasTranslated) {
      setDelay(undefined)
      setRatio(100)
    } else {
      setDelay(1000)
      setRatio(0)
    }
  }, [hasTranslated])

  return { ratio }
}

export function useScoreChartData(project: ProjectModels.ProjectDetail) {
  const { t, i18n } = useTranslation()
  const FIRST_SCORE_CHART_DATA = [
    {
      name: t('dashboard.projectDetailPage.videoView.eyeMovementRatio'),
      score: project.globalMetrics.emr.score,
      meanScore: project.globalMetrics.emr.meanScore,
    },
    {
      name: t('dashboard.projectDetailPage.videoView.emotionRecognition'),
      score: project.globalMetrics.emotion.score,
      meanScore: project.globalMetrics.emotion.meanScore,
    },
    {
      name: t('dashboard.projectDetailPage.videoView.cognitiveEngagement'),
      score: project.globalMetrics.cognitive.score,
      meanScore: project.globalMetrics.cognitive.meanScore,
    },
  ]
  const SECOND_SCORE_CHART_DATA = [
    {
      name: t('dashboard.projectDetailPage.storyboardView.theGolden3Seconds'),
      score: project.dimensionMetrics.firstThree.score,
      meanScore: project.dimensionMetrics.firstThree.meanScore,
    },
    {
      name: t('dashboard.projectDetailPage.storyboardView.informationTransmission'),
      score: project.dimensionMetrics.keyInformation.overall.score,
      meanScore: project.dimensionMetrics.keyInformation.overall.meanScore,
    },
    {
      name: t('dashboard.projectDetailPage.storyboardView.brandRelevance'),
      score: project.dimensionMetrics.brandConnection.overall.score,
      meanScore: project.dimensionMetrics.brandConnection.overall.meanScore,
    },
    {
      name: t('dashboard.projectDetailPage.storyboardView.interestStimulation'),
      score: project.dimensionMetrics.interestingEnagement.overall.score,
      meanScore: project.dimensionMetrics.interestingEnagement.overall.meanScore,
    },
    {
      name: t('dashboard.projectDetailPage.storyboardView.theEnding3Seconds'),
      score: project.dimensionMetrics.lastThree.score,
      meanScore: project.dimensionMetrics.lastThree.meanScore,
    },
  ]
  const [firstScoreChart, setFirstScoreChart] = useState<{
    color: string
    datas: { name: string; score: number; meanScore: number }[]
  }>({
    color: '#8B77FB',
    datas: FIRST_SCORE_CHART_DATA,
  })
  const [secondScoreChart, setSecondScoreChart] = useState<{
    color: string
    datas: { name: string; score: number; meanScore: number }[]
  }>({
    color: '#63B0F9',
    datas: SECOND_SCORE_CHART_DATA,
  })

  useEffect(() => {
    setFirstScoreChart({
      color: '#8B77FB',
      datas: [...FIRST_SCORE_CHART_DATA],
    })
    setSecondScoreChart({
      color: '#63B0F9',
      datas: [...SECOND_SCORE_CHART_DATA],
    })
  }, [i18n.language])
  return { firstScoreChart, secondScoreChart }
}

export const VideoActionContext = createContext<{
  lastestVideoInstances: Player[]
  setLastestVideoInstances: (instance: Player[]) => void
  videoBlobUrl: string | null
}>({
  lastestVideoInstances: [],
  setLastestVideoInstances: () => { },
  videoBlobUrl: null,
})

function VideoAnalysis(props: {
  project: ProjectModels.ProjectDetail
  hasTranslated: boolean
  progressRatio: any
  needDoAnimation: boolean
  onAnimationEnd: () => void
  mode?: 'professor' | 'default'
}) {
  const componentId = useId()
  const { t, i18n } = useTranslation()
  const { showProfessorPanel, unfinishedRateBtn } = useContext(ProfessorContext)
  const POPOVER_DESC = [
    {
      title: t('dashboard.projectDetailPage.subAnalysisDescription.norm'),
      description: t('dashboard.projectDetailPage.subAnalysisDescription.normDesc'),
    },
    {
      title: t('dashboard.projectDetailPage.subAnalysisDescription.attentionIndex'),
      description: t('dashboard.projectDetailPage.subAnalysisDescription.attentionIndexDesc'),
    },
    {
      title: t('dashboard.projectDetailPage.subAnalysisDescription.emotionIndex'),
      description: t('dashboard.projectDetailPage.subAnalysisDescription.emotionIndexDesc'),
    },
    {
      title: t('dashboard.projectDetailPage.subAnalysisDescription.cognitionIndex'),
      description: t('dashboard.projectDetailPage.subAnalysisDescription.cognitionIndexDesc'),
    },
    {
      title: t('dashboard.projectDetailPage.subAnalysisDescription.theGolden3Seconds'),
      description: t('dashboard.projectDetailPage.subAnalysisDescription.theGolden3SecondsDesc'),
    },
    {
      title: t('dashboard.projectDetailPage.subAnalysisDescription.informationTransmission'),
      description: t(
        'dashboard.projectDetailPage.subAnalysisDescription.informationTransmissionDesc'
      ),
    },
    {
      title: t('dashboard.projectDetailPage.subAnalysisDescription.brandRelevance'),
      description: t('dashboard.projectDetailPage.subAnalysisDescription.brandRelevanceDesc'),
    },
    {
      title: t('dashboard.projectDetailPage.subAnalysisDescription.interestStimulation'),
      description: t('dashboard.projectDetailPage.subAnalysisDescription.interestStimulationDesc'),
    },
    {
      title: t('dashboard.projectDetailPage.subAnalysisDescription.theEnding3Seconds'),
      description: t('dashboard.projectDetailPage.subAnalysisDescription.theEnding3SecondsDesc'),
    },
  ]

  const { ANALYSIS_LABELS } = useAnalysisLabels(props.hasTranslated)

  const [second, setSecond] = useState<number | undefined>(undefined)

  // const { EYE_MOVEMENT_RATIO, COGNITIVE_ENGAGEMENT, EMOTION_RECOGNITION } = useTranslateDimension()

  /*   const [, setScores] = useState<
    Array<{ score: number; title: string; meanScore: number; icon: ReactNode }>
  >([]) */


  const [ratio, setRatio] = useState<number>(0)
  const { ratio: translateRatio } = useTranslateProgress(props.hasTranslated)

  const { firstScoreChart, secondScoreChart } = useScoreChartData(props.project)
  const [chartRerender, setChartRerender] = useState<boolean>(true)
  const [animationCollector, setAnimationCollector] = useState<boolean[]>([])

  const [viewMode, setViewMode] = useState<'realtime' | 'panoramic'>('realtime')

  const [lastestVideoInstances, setLastestVideoInstances] = useState<Player[]>([])
  const [mainVideoInstance, setMainVideoInstance] = useState<Player | null>(null)
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null)
  const isPortraitMode = isPortraitByProjectInfo(props.project)

  useEffect(() => {
    if (unfinishedRateBtn.length > 0) {
      const el = document.getElementById(`${PROFESSOR_RATE_BTN_ID_PREFIX}-${unfinishedRateBtn[0]}`)
      if (!el) {
        return
      }
      const containerElement = document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID)
      const categoryTabElement = document.getElementById(CATEGORY_TAB_ID)!
      if (containerElement) {
        const top =
          el.getBoundingClientRect().top -
          categoryTabElement.offsetTop -
          categoryTabElement.offsetHeight
        const deltaTop = unfinishedRateBtn[0] === 'adEffIndex' ? 90 : 0
        containerElement.scrollTo({
          top: top + containerElement.scrollTop - deltaTop,
          behavior: 'smooth',
        })
      }
    }
  }, [unfinishedRateBtn])

  useEffect(() => {
    setChartRerender(false)
    setTimeout(() => {
      setChartRerender(true)
    })
  }, [i18n.language])

  useEffect(() => {
    setRatio(props.progressRatio)
  }, [props.progressRatio])

  useEffect(() => {
    setRatio(translateRatio)
  }, [translateRatio])

  useEffect(() => {
    // 总共是 4 个部分
    if (animationCollector.length >= 4) {
      props.onAnimationEnd()
    }
  }, [animationCollector])

  useEventListener('keydown', (e) => {
    if (e.code === 'Space') {
      e.preventDefault()
      if (lastestVideoInstances.length > 0) {
        lastestVideoInstances.forEach(instance => {
          if (instance.paused) {
            instance.play()
          } else {
            instance.pause()
          }
        })
      }
    }

  }, { target: document })

  useEffect(() => {
    if (mainVideoInstance) {
      mainVideoInstance.on('play', () => {
        if (!lastestVideoInstances.includes(mainVideoInstance)) {
          lastestVideoInstances.forEach(instance => {
            instance.pause()
          })
        }
        setLastestVideoInstances([mainVideoInstance])
      })
    }
  }, [mainVideoInstance, lastestVideoInstances])

  useEffect(() => {
    // blobGet(props.project.adUrl, {}).send().then((res) => {
    //   const blob = (res as any).data as Blob
    //   const url = URL.createObjectURL(blob)
    //   setVideoBlobUrl(url)
    // }).catch((err) => {
    //   // 如果获取失败，降级使用原始URL
    //   console.error(err)
    //   setVideoBlobUrl(props.project.adUrl)
    // })
    setVideoBlobUrl(props.project.adUrl)
  }, [props.project.adUrl])

  return (
    <div className={styles.videoAnalysis}>
      <div className={cls(styles.videoWrapper, { [styles.portrait]: isPortraitMode })}>
        <div className={styles.videoBox}>
          <VideoPlayer
            key={`main-video-player-${componentId}`}
            id={`main-video-player-${componentId}`}
            url={videoBlobUrl || props.project.adUrl}
            currentTime={second}
            onVideoReady={(instance) => {
              setMainVideoInstance(instance)
            }}
          />
        </div>
        <div className={styles.score}>
          <ScoreCard
            score={props.project.globalMetrics.overall.score}
            meanScore={props.project.globalMetrics.overall.meanScore}
            mode={props.mode}
          />
        </div>
      </div>
      <VideoActionContext.Provider value={{ lastestVideoInstances, setLastestVideoInstances, videoBlobUrl }}>
        {isPortraitMode ? (
          <SubjectiveResponseCurvePortrait
            chartRerender={chartRerender}
            mode={props.mode}
            project={props.project}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onAxisClick={setSecond}
            onRateClick={() => {
              showProfessorPanel('subjective_response_curve')
            }}
          />
        ) : (
          <SubjectiveResponseCurveLandscape
            chartRerender={chartRerender}
            mode={props.mode}
            project={props.project}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onAxisClick={setSecond}
            onRateClick={() => {
              showProfessorPanel('subjective_response_curve')
            }}
          />
        )}
      </VideoActionContext.Provider>
      {/* 测试图表 */}
      {/* <TestChart project={props.project} /> */}
      <div className={styles.divider}></div>
      <div className={styles.scoreChartTitle}>
        <span className={styles.leftTitle}>
          <FormTitle
            style={{ marginBottom: 0 }}
            text={
              <>
                {t('dashboard.projectDetailPage.videoView.indexAnalysis')}
                {props.mode === 'professor' && (
                  <span id={`${PROFESSOR_RATE_BTN_ID_PREFIX}-indexAnalyses`}>
                    <RateBtn
                      rateKey={'index_analyses'}
                      style={{ marginLeft: 8 }}
                      onClick={() => {
                        showProfessorPanel('index_analyses')
                      }}
                    />
                  </span>
                )}
              </>
            }
          />
          <SubAnalysisDescriptionPopover descriptions={POPOVER_DESC} />
        </span>
        <span className={styles.right}>
          <span className={styles.line}></span>
          <span className={styles.text}>{t('dashboard.projectDetailPage.videoView.norm')}</span>
        </span>
      </div>

      <div className={styles.rateSummary}>
        <VideoScoreBarChart
          overallDatas={firstScoreChart.datas}
          periodDatas={secondScoreChart.datas}
        />

        {/*         <div className={styles.rateSummaryOne}>
          {chartRerender && (
            <VideoScoreChart color={firstScoreChart.color} datas={firstScoreChart.datas} />
          )}
        </div>
        <div className={styles.rateSummaryTwo}>
          {chartRerender && (
            <VideoScoreChart color={secondScoreChart.color} datas={secondScoreChart.datas} />
          )}
        </div> */}
      </div>
      <div className={styles.analysisStepBox} style={{ width: '65%' }}>
        <AnalysisStep ratio={ratio} labels={ANALYSIS_LABELS} />
      </div>
      <div className={styles.analysisSummary}>
        <div className={styles.analysisSummaryTitle}>
          <AIcon width={28} type='icon-line-global_ai' />
          <span className={styles.analysisSummaryTitleText}>
            <MiddleText text={t('dashboard.projectDetailPage.videoView.keyResult')} />
          </span>
          {props.mode === 'professor' && (
            <span id={`${PROFESSOR_RATE_BTN_ID_PREFIX}-keyResults`}>
              <RateBtn
                rateKey={'key_results'}
                style={{ marginLeft: 8 }}
                onClick={() => {
                  showProfessorPanel('key_results')
                }}
              />
            </span>
          )}
        </div>
        <div className={cls(styles.analysisSummaryContent)}>
          <MarkdownRenderer
            loading={!props.hasTranslated}
            markdownText={props.project.globalMetrics.reason.performance}
            needAnimation={props.needDoAnimation}
            animationEnd={() => {
              setAnimationCollector((prev) => [...prev, true])
            }}
          />
        </div>
        {
          props.project.isFreeTrial && <div className={cls(styles.analysisSummaryContent)}>
            <HiddenDetailBox />
          </div>
        }
        {
          props.project.globalMetrics.reason.advantage &&
          <div className={cls(styles.analysisSummaryContent)}>
            <div className={styles.summaryTitle}>
              {t('dashboard.projectDetailPage.videoView.advantage')}
            </div>
            <div>
              <MarkdownRenderer
                loading={!props.hasTranslated}
                markdownText={props.project.globalMetrics.reason.advantage}
                needAnimation={props.needDoAnimation}
                animationEnd={() => {
                  setAnimationCollector((prev) => [...prev, true])
                }}
              />
            </div>
          </div>
        }
        {
          props.project.globalMetrics.reason.disadvantage &&
          <div className={cls(styles.analysisSummaryContent)}>
            <div className={styles.summaryTitle}>
              {t('dashboard.projectDetailPage.videoView.disadvantage')}
            </div>
            <div>
              <MarkdownRenderer
                loading={!props.hasTranslated}
                markdownText={props.project.globalMetrics.reason.disadvantage}
                needAnimation={props.needDoAnimation}
                animationEnd={() => {
                  setAnimationCollector((prev) => [...prev, true])
                }}
              />
            </div>
          </div>
        }
        {
          props.project.globalMetrics.reason.suggestions && <>
            <div className={styles.analysisSummaryTitle} style={{ marginTop: 24 }}>
              <AIcon width={28} type='icon-fill-global_suggestion' />
              <span className={styles.analysisSummaryTitleText}>
                <MiddleText text={t('dashboard.projectDetailPage.videoView.suggestions')} />
              </span>
            </div>
            <div className={cls(styles.analysisSummaryContent)}>
              <MarkdownRenderer
                loading={!props.hasTranslated}
                markdownText={props.project.globalMetrics.reason.suggestions}
                needAnimation={props.needDoAnimation}
                animationEnd={() => {
                  setAnimationCollector((prev) => [...prev, true])
                }}
              />
            </div>
          </>
        }
      </div>
      {
        INTERNAL_HOST.includes(location.hostname) &&
        <div className={styles.aiTipBox} style={{ backgroundColor: '#fff' }}>
          <AiTip style={{ marginTop: 20, }} />
        </div>
      }
    </div>
  )
}

export default VideoAnalysis
